import React, { useMemo, useState } from "react";
import { ArrowDownTrayIcon, ClipboardDocumentIcon, CheckIcon } from "@heroicons/react/24/outline";
import type { Deal, Document } from "./types/redux";

interface InlineDealsTableProps {
  deals: Deal[];
  documents?: Document[];
  onDealClick: (deal: Deal) => void;
}

/** Column definition for the inline table */
interface ColumnDef {
  key: string;
  header: string;
  className?: string;
  cellClassName?: string;
  render: (deal: Deal) => React.ReactNode;
  exportValue: (deal: Deal) => string;
}

const MAX_VISIBLE_ROWS = 10;

/** Get the best display name for a deal */
function getDealName(deal: Deal): string {
  return deal.dealName || deal.name || "--";
}

/** Get portfolio company name (or "Sensitive" mask) */
function getCompany(deal: Deal): string {
  if ((deal.sensitivity as string)?.toLowerCase() === "true") return "Sensitive";
  return deal.portfolioCompanyName || "--";
}

function getSector(deal: Deal): string {
  return (deal.sector as string) || "";
}

function getCountry(deal: Deal): string {
  return (deal.country as string) || "";
}

function getYear(deal: Deal): string {
  const y = deal.year as string | number | undefined;
  if (y == null || y === "") return "";
  return String(y);
}

function getStage(deal: Deal): string {
  return (deal.stage as string) || (deal.dealStage as string) || "";
}

/** Get document count from deal, cross-referencing payload-level documents */
function getDocCount(deal: Deal, documents?: Document[]): number {
  const count = deal.documentCount as number | undefined;
  if (typeof count === "number" && count > 0) return count;
  const embeddedDocs = deal.documents as unknown[] | undefined;
  if (Array.isArray(embeddedDocs) && embeddedDocs.length > 0) return embeddedDocs.length;
  if (documents && deal.dealId) {
    const matched = documents.filter((d) => d.dealId === deal.dealId);
    if (matched.length > 0) return matched.length;
  }
  return 0;
}

/** Get description text */
function getDescription(deal: Deal): string {
  return (deal.business as string) || (deal.description as string) || "";
}

function getDealType(deal: Deal): string {
  return (deal.dealType as string) || "PE";
}

function getDealTypeLabel(deal: Deal): string {
  return getDealType(deal) === "PC" ? "Credit" : "PE";
}

function dashIfEmpty(value: string): React.ReactNode {
  if (!value) return <span className="text-gray-400">--</span>;
  return value;
}

export default function InlineDealsTable_Component({ deals, documents, onDealClick }: InlineDealsTableProps): JSX.Element | null {
  const [copied, setCopied] = useState<boolean>(false);

  const columns = useMemo<ColumnDef[]>(() => [
    {
      key: "dealName",
      header: "Deal Name",
      className: "min-w-[140px]",
      cellClassName: "truncate",
      render: (deal) => {
        const isPC = getDealType(deal) === "PC";
        return (
          <span className={`${isPC ? "text-cvc-pc bg-cvc-pcBg" : "text-cvc-blue bg-cvc-linkBg"} rounded-[3px] px-1 cursor-pointer hover:underline`}>
            {getDealName(deal)}
          </span>
        );
      },
      exportValue: getDealName,
    },
    {
      key: "type",
      header: "Type",
      className: "w-[60px]",
      render: (deal) => {
        const isPC = getDealType(deal) === "PC";
        return (
          <span className={`inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-semibold rounded-full ${isPC ? "bg-cvc-pcBg text-cvc-pc" : "bg-cvc-linkBg text-cvc-blue"}`}>
            {getDealTypeLabel(deal)}
          </span>
        );
      },
      exportValue: getDealTypeLabel,
    },
    {
      key: "company",
      header: "Company",
      className: "min-w-[120px]",
      cellClassName: "truncate",
      render: (deal) => getCompany(deal),
      exportValue: getCompany,
    },
    {
      key: "sector",
      header: "Sector",
      className: "min-w-[110px] hidden desktop:table-cell",
      cellClassName: "truncate hidden desktop:table-cell",
      render: (deal) => dashIfEmpty(getSector(deal)),
      exportValue: getSector,
    },
    {
      key: "country",
      header: "Country",
      className: "min-w-[90px]",
      cellClassName: "truncate",
      render: (deal) => dashIfEmpty(getCountry(deal)),
      exportValue: getCountry,
    },
    {
      key: "year",
      header: "Year",
      className: "w-[70px]",
      cellClassName: "truncate",
      render: (deal) => dashIfEmpty(getYear(deal)),
      exportValue: getYear,
    },
    {
      key: "stage",
      header: "Stage",
      className: "min-w-[90px]",
      cellClassName: "truncate",
      render: (deal) => dashIfEmpty(getStage(deal)),
      exportValue: getStage,
    },
    {
      key: "docs",
      header: "Docs",
      className: "w-[60px] text-center",
      render: (deal) => {
        const count = getDocCount(deal, documents);
        if (count === 0) return <span className="text-gray-400">--</span>;
        return (
          <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-cvc-lb2 text-cvc-blue rounded-full">
            {count}
          </span>
        );
      },
      exportValue: (deal) => String(getDocCount(deal, documents)),
    },
    {
      key: "description",
      header: "Description",
      className: "min-w-[200px] hidden desktop:table-cell",
      cellClassName: "truncate hidden desktop:table-cell",
      render: (deal) => {
        const desc = getDescription(deal);
        if (!desc) return <span className="text-gray-400">No description</span>;
        return <span title={desc}>{desc}</span>;
      },
      exportValue: getDescription,
    },
  ], [documents]);

  if (deals.length === 0) return null;

  /** Build a delimited string from the current column set (used for TSV/CSV) */
  const buildExportRows = (): string[][] => {
    const headers = columns.map((c) => c.header);
    const rows = deals.map((d) => columns.map((c) => c.exportValue(d)));
    return [headers, ...rows];
  };

  const handleExport = () => {
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csv = buildExportRows().map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "deals.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    const tsv = buildExportRows().map((r) => r.join("\t")).join("\n");
    try {
      await navigator.clipboard.writeText(tsv);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = tsv;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const isTruncated = deals.length > MAX_VISIBLE_ROWS;

  return (
    <div className="mt-3 mb-1 border border-cvc-tableBorder rounded-[5px] overflow-hidden">
      {/* Cap visible rows at ~10; thead stays sticky while body scrolls. */}
      <div className="overflow-auto max-h-[440px]">
        <table className="min-w-full divide-y divide-cvc-tableBorder">
          <thead className="bg-cvc-tableHeader sticky top-0 z-10">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={`py-2 px-3 text-left text-xs font-cvcBody font-normal text-cvc-headerText uppercase tracking-wider ${col.className || ""}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-cvc-tableBorder font-cvcBody">
            {deals.map((deal, idx) => (
              <tr
                key={deal.dealId || deal.id || idx}
                className="hover:bg-cvc-lb2 transition-colors duration-150 cursor-pointer"
                onClick={() => onDealClick(deal)}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`py-2 px-3 text-sm text-cvc-text ${col.cellClassName || ""} ${col.className || ""}`}
                  >
                    {col.render(deal)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Action row */}
      <div className="flex items-center gap-3 px-3 py-2 bg-cvc-tableHeader border-t border-cvc-tableBorder">
        <button
          onClick={(e) => { e.stopPropagation(); handleExport(); }}
          className="inline-flex items-center gap-1.5 text-xs text-cvc-headerText hover:text-cvc-blue transition-colors"
        >
          <ArrowDownTrayIcon className="w-3.5 h-3.5" />
          Export to Excel
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); void handleCopy(); }}
          className="inline-flex items-center gap-1.5 text-xs text-cvc-headerText hover:text-cvc-blue transition-colors"
          aria-live="polite"
        >
          {copied ? (
            <>
              <CheckIcon className="w-3.5 h-3.5 text-cvc-green" />
              <span className="text-cvc-green">Copied</span>
            </>
          ) : (
            <>
              <ClipboardDocumentIcon className="w-3.5 h-3.5" />
              Copy table
            </>
          )}
        </button>
        {isTruncated && (
          <span className="ml-auto text-xs text-cvc-headerText">
            Showing {MAX_VISIBLE_ROWS} of {deals.length} — scroll for more
          </span>
        )}
      </div>
    </div>
  );
}
